import { useMemo, useRef, type ReactNode } from "react";
import { ApolloProvider } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { useAuthContext } from "../../auth/AuthContext";
import { useActiveAccessToken } from "../../auth/useActiveAccessToken";
import { getStoredTokens } from "../../auth/b2cClient";
import { createMember360Client } from "./client";

/**
 * Builds the Apollo client once and wires its authLink to the latest token via
 * a stable getter — so the client survives token refreshes (MSAL silent or
 * bridge updates) without being re-created.
 *
 * onAuthFailure is a stable ref-backed callback so the client doesn't get
 * rebuilt on every render when `logout` / `navigate` identities change.
 *
 * getPpToken: reads the Provider Portal B2C access token so the Apollo
 * authLink can forward it as `x-pp-bearer` to care-management-gql's CBP
 * resolvers. When the user logged in via provider-portal-b2c, their main
 * `accessToken` IS the PP token, so we return that. When they're on Entra or
 * pathways-b2c, we check localStorage for any separately-stored PP B2C
 * session. Empty when no PP session exists — the header is omitted.
 */
export function Member360ApolloProvider({ children }: { children: ReactNode }) {
  const { getToken } = useActiveAccessToken();
  const { logout, selectedProvider, accessToken } = useAuthContext();
  const navigate = useNavigate();

  const handlerRef = useRef<() => void>(() => {});
  handlerRef.current = () => {
    void logout();
    if (window.location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
  };
  const onAuthFailure = useRef(() => handlerRef.current()).current;

  // Stable getter for the bearer token — read on every Apollo request.
  // `useActiveAccessToken().getToken` is a fresh arrow each render; if we fed
  // it directly to the client useMemo below, the client (and its InMemoryCache)
  // would be rebuilt on every render. A rebuilt cache drops connectCurrentUser,
  // which flips useCurrentUser().role to null mid-navigation and makes
  // ProtectedRoute blank the whole admin subtree (nav + header + content) on
  // every route change. Pin the identity via a ref like getPpToken below so the
  // client is built ONCE and the cache survives navigation. The authLink still
  // reads the latest token live per-request (client.ts), so token refreshes are
  // unaffected.
  const tokenGetterRef = useRef<() => string | undefined>(() => undefined);
  tokenGetterRef.current = getToken;
  const stableGetToken = useRef(() => tokenGetterRef.current()).current;

  // Stable getter for the PP B2C token — read on every Apollo request.
  // Using a ref so the getter identity is stable and doesn't trigger
  // client recreation on every render.
  const ppGetterRef = useRef<() => string | null | undefined>(() => undefined);
  ppGetterRef.current = () => {
    if (selectedProvider === "provider-portal-b2c") {
      // The current accessToken is already the PP B2C token.
      return accessToken;
    }
    // For other providers, fall back to any separately-stored PP B2C session
    // (e.g. the user previously logged in with PP B2C and left those tokens
    // in localStorage while switching to Entra for this session).
    return getStoredTokens("provider-portal-b2c")?.access_token ?? null;
  };
  const getPpToken = useRef(() => ppGetterRef.current()).current;

  const client = useMemo(
    () => createMember360Client({ getToken: stableGetToken, getPpToken, onAuthFailure }),
    [stableGetToken, getPpToken, onAuthFailure],
  );
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
